import { useMemo, useRef, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { servicesAPI, ordersAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import AdsDisplay from '../components/AdsDisplay';
import { AlertCircle, Search, X, Check, ArrowRight, Folder, Zap, Layers, ChevronDown } from 'react-feather';
import { formatCurrencyWithConversion } from '../utils/currency';
import { getServiceColor, getServiceIcon, getServiceIconAsset, getServiceToken } from '../utils/serviceIcons';
import PlatformIcon, { detectPlatform } from '../components/PlatformIcon';
import { getServiceCategory, detectSubCategory } from './Services';
import './NewOrder.css';

const QUANTITY_PRESETS = [100, 500, 1000, 2500, 5000, 10000, 25000, 50000];

function normalizeProvidedAverageTime(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function estimateDeliveryTime(service, quantity) {
  if (!service) return 'Not provided';

  const providedAverageTime = normalizeProvidedAverageTime(service.average_time);
  if (providedAverageTime && !providedAverageTime.toLowerCase().startsWith('estimated')) {
    return providedAverageTime;
  }

  const min = Math.max(1, Number.parseInt(service.min, 10) || 1);
  const max = Math.max(min, Number.parseInt(service.max, 10) || min);
  const selectedQuantity = Math.max(min, Number.parseInt(quantity, 10) || min);
  const clampedQuantity = Math.min(selectedQuantity, max);
  const minMinutes = 5;
  const maxMinutes = 30;

  if (max <= min) {
    return `Estimated ${minMinutes} minutes`;
  }

  const progress = Math.max(0, Math.min(1, (clampedQuantity - min) / (max - min)));
  const estimatedMinutes = Math.round(minMinutes + ((maxMinutes - minMinutes) * progress));

  return `Estimated ${estimatedMinutes} minutes`;
}

export default function NewOrder() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [servicesMap, setServicesMap] = useState({});
  const [flat, setFlat] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [form, setForm] = useState({ service_id: '', link: '', quantity: '', comments: '' });
  const [selectedService, setSelectedService] = useState(null);
  const [costUSD, setCostUSD] = useState(0);
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const commentNumbersRef = useRef(null);
  const categoryPickerRef = useRef(null);
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();

  // Close category suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (categoryPickerRef.current && !categoryPickerRef.current.contains(event.target)) {
        setShowCategorySuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch all services & sanitize categories so every service ALWAYS has a valid category
  useEffect(() => {
    servicesAPI.getAll()
      .then(r => {
        const rawFlat = r.data?.flat || [];
        // Ensure EVERY service has a guaranteed, non-empty category
        const cleanFlat = rawFlat.map(s => {
          const cat = getServiceCategory(s);
          return {
            ...s,
            category: cat,
            final_price: parseFloat(s.final_price || s.price_per_1000 || 0),
          };
        });
        setFlat(cleanFlat);

        // Group into servicesMap by category
        const map = {};
        cleanFlat.forEach(svc => {
          const cat = svc.category;
          if (!map[cat]) map[cat] = [];
          map[cat].push(svc);
        });
        setServicesMap(map);

        const cats = Object.keys(map);
        setCategories(cats);

        // Resolve requested category & service
        const requestedCategory = searchParams.get('category');
        const requestedServiceId = searchParams.get('service_id');
        let initialService = null;
        let initialCat = cats[0] || '';

        if (requestedServiceId) {
          const matched = cleanFlat.find(s => String(s.id) === String(requestedServiceId));
          if (matched) {
            initialService = matched;
            initialCat = matched.category;
          }
        } else if (requestedCategory) {
          const matchedCat = cats.find(c => c.toLowerCase() === requestedCategory.toLowerCase()) ||
                             cats.find(c => c.toLowerCase().includes(requestedCategory.toLowerCase()));
          if (matchedCat) {
            initialCat = matchedCat;
            const catServices = map[matchedCat] || [];
            if (catServices.length > 0) initialService = catServices[0];
          }
        } else if (cats.length > 0) {
          const firstCatServices = map[cats[0]] || [];
          if (firstCatServices.length > 0) initialService = firstCatServices[0];
        }

        setSelectedCategory(initialCat);
        setSearchQuery(initialCat);

        if (initialService) {
          setSelectedService(initialService);
          setForm(f => ({
            ...f,
            service_id: String(initialService.id),
            quantity: String(initialService.min || 1000),
          }));
        }
      })
      .catch(() => setError('Failed to load services.'))
      .finally(() => setServicesLoading(false));
  }, []);

  // Update selectedService & cost when service_id or quantity changes
  useEffect(() => {
    if (form.service_id && flat.length > 0) {
      const svc = flat.find(s => String(s.id) === String(form.service_id));
      if (svc) {
        setSelectedService(svc);
        // GUARANTEE: Service category is ALWAYS synchronized with the selected service
        if (svc.category && svc.category !== selectedCategory) {
          setSelectedCategory(svc.category);
          setSearchQuery(svc.category);
        }
      }
    }
  }, [form.service_id, flat, selectedCategory]);

  useEffect(() => {
    if (selectedService && form.quantity) {
      const qty = parseInt(form.quantity, 10) || 0;
      const priceUSD = parseFloat(selectedService.final_price) || 0;
      setCostUSD((qty / 1000) * priceUSD);
    } else {
      setCostUSD(0);
    }
  }, [selectedService, form.quantity]);

  const balanceUSD = parseFloat(user?.balance || 0);
  const canAfford = costUSD > 0 && balanceUSD >= costUSD;
  const qty = parseInt(form.quantity, 10) || 0;
  const qtyValid = selectedService
    ? qty >= selectedService.min && qty <= selectedService.max
    : true;
  const deliveryTimeLabel = selectedService
    ? estimateDeliveryTime(selectedService, form.quantity)
    : 'Not provided';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedService) {
      setError('Please choose a service for your order.');
      return;
    }

    if (!qtyValid) {
      setError(`Quantity must be between ${selectedService.min.toLocaleString()} and ${selectedService.max.toLocaleString()}`);
      return;
    }

    if (selectedService?.custom_comments_enabled && !form.comments.trim()) {
      setError('Custom comments are required for this service');
      return;
    }

    if (!canAfford) {
      setError(`Insufficient balance. You need ${formatCurrencyWithConversion(costUSD, user?.currency || 'USD', 4)} but have ${formatCurrencyWithConversion(balanceUSD, user?.currency || 'USD', 4)}`);
      return;
    }

    setLoading(true);
    try {
      const res = await ordersAPI.place({
        service_id: parseInt(form.service_id, 10),
        link: form.link,
        quantity: qty,
        comments: form.comments
      });

      const discountNote = res.data.referral_discount_applied
        ? ` | Referral discount: ${res.data.referral_discount_percent}% saved ${formatCurrencyWithConversion(parseFloat(res.data.referral_discount_amount || 0), user?.currency || 'USD', 4)}`
        : '';
      setSuccess(`Order #${res.data.order_id} placed successfully. Cost: ${formatCurrencyWithConversion(parseFloat(res.data.cost), user?.currency || 'USD', 4)} | New balance: ${formatCurrencyWithConversion(parseFloat(res.data.new_balance), user?.currency || 'USD', 4)}${discountNote}`);
      setForm(f => ({ ...f, link: '', quantity: selectedService ? String(selectedService.min) : '', comments: '' }));
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const categoryServices = servicesMap[selectedCategory] || [];
  // Ensure selectedService is ALWAYS included in filteredServices even if map indexing differs
  const filteredServices = useMemo(() => {
    if (selectedService && !categoryServices.some(s => s.id === selectedService.id)) {
      return [selectedService, ...categoryServices];
    }
    return categoryServices;
  }, [categoryServices, selectedService]);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredCategories = normalizedSearch
    ? categories.filter((category) => category.toLowerCase().includes(normalizedSearch))
    : categories;

  const commentLineNumbers = useMemo(() => {
    const lineCount = Math.max(1, form.comments.split('\n').length);
    return Array.from({ length: lineCount }, (_, index) => index + 1);
  }, [form.comments]);

  const enteredCommentCount = form.comments
    .split('\n')
    .filter(comment => comment.trim()).length;

  const handleCommentsScroll = (event) => {
    if (commentNumbersRef.current) {
      commentNumbersRef.current.scrollTop = event.currentTarget.scrollTop;
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSearchQuery(category);
    setShowCategorySuggestions(false);
    const catServices = servicesMap[category] || [];
    if (catServices.length > 0) {
      const firstSvc = catServices[0];
      setSelectedService(firstSvc);
      setForm(f => ({
        ...f,
        service_id: String(firstSvc.id),
        quantity: String(firstSvc.min || 1000),
      }));
      setSearchParams({ category, service_id: String(firstSvc.id) }, { replace: true });
    }
  };

  const handleServiceChange = (nextServiceId) => {
    setForm(f => ({ ...f, service_id: nextServiceId }));
    const matched = flat.find(s => String(s.id) === String(nextServiceId));
    if (matched) {
      setSelectedService(matched);
      // Guarantee that the category is always updated to match this service!
      if (matched.category) {
        setSelectedCategory(matched.category);
        setSearchQuery(matched.category);
      }
      setSearchParams({ category: matched.category, service_id: String(matched.id) }, { replace: true });
    }
  };

  const activeCategoryName = selectedService?.category || selectedCategory || 'Choose Category';
  const activePlatform = detectPlatform(activeCategoryName);

  return (
    <div className="new-order-page">
      <div className="new-order-header">
        <h1 className="page-title">New Order</h1>
        <p className="page-subtitle">Select a service and boost your social media presence</p>
      </div>

      <div className="alert alert-info new-order-alert">
        <Zap size={16} />
        <span>Avoid placing duplicate orders for the same link simultaneously. Wait for the order to start before ordering again.</span>
      </div>

      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />
      <AdsDisplay adSlot="banner-320x50" className="banner" />

      <div className="new-order-grid">
        <div className="card new-order-form-card">
          {error && <div className="alert alert-error">{error}</div>}
          {success && (
            <div className="alert alert-success">
              {success}
              <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                <Link to="/dashboard/orders" className="btn btn-secondary btn-sm">View Orders</Link>
                <button className="btn btn-secondary btn-sm" onClick={() => setSuccess('')}>Place Another</button>
              </div>
            </div>
          )}

          {servicesLoading ? (
            <div className="new-order-loading">
              <div className="spinner" />
              <p>Loading services catalog...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="new-order-form">
              {/* ── 1. Service Category (Always Available & Visible) ── */}
              <div className="form-group" ref={categoryPickerRef}>
                <div className="new-order-label-row">
                  <label className="form-label">Service Category</label>
                  <span className="new-order-category-count-badge">
                    {categories.length} categories available
                  </span>
                </div>

                {/* Current Active Category Pill Card */}
                <div
                  className="new-order-active-category-card"
                  onClick={() => setShowCategorySuggestions(!showCategorySuggestions)}
                  title="Click to search or change category"
                >
                  <div className="active-cat-left">
                    <PlatformIcon platform={activePlatform} size={32} iconSize={16} />
                    <div className="active-cat-info">
                      <span className="active-cat-title">{activeCategoryName}</span>
                      <span className="active-cat-subtitle">
                        {categoryServices.length} {categoryServices.length === 1 ? 'service' : 'services'} available
                      </span>
                    </div>
                  </div>
                  <div className="active-cat-right">
                    <span className="active-cat-change-pill">Change</span>
                    <ChevronDown size={16} className={`cat-chevron ${showCategorySuggestions ? 'open' : ''}`} />
                  </div>
                </div>

                {/* Category Search & Dropdown Suggestions */}
                {showCategorySuggestions && (
                  <div className="new-order-category-dropdown">
                    <div className="new-order-category-search-bar">
                      <Search size={16} className="cat-search-icon" />
                      <input
                        className="cat-search-input"
                        type="text"
                        placeholder="Search category (e.g. Instagram, TikTok, YouTube)..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="cat-search-clear"
                          onClick={() => setSearchQuery('')}
                        >
                          <X size={15} />
                        </button>
                      )}
                    </div>

                    <div className="new-order-category-suggestions">
                      {filteredCategories.length > 0 ? (
                        filteredCategories.map((category) => {
                          const isSelected = category === selectedCategory;
                          const plat = detectPlatform(category);
                          const count = servicesMap[category]?.length || 0;
                          return (
                            <button
                              key={category}
                              type="button"
                              className={`new-order-category-suggestion ${isSelected ? 'active' : ''}`}
                              onClick={() => handleCategorySelect(category)}
                            >
                              <PlatformIcon platform={plat} size={28} iconSize={14} />
                              <div className="suggestion-text">
                                <span className="suggestion-title">{category}</span>
                                <span className="suggestion-count">{count} services</span>
                              </div>
                              {isSelected && <Check size={16} className="suggestion-check" />}
                            </button>
                          );
                        })
                      ) : (
                        <div className="new-order-category-empty">
                          No categories matched "{searchQuery}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ── 2. Service Selection ── */}
              <div className="form-group">
                <label className="form-label">Service</label>
                <select
                  className="form-select new-order-service-select"
                  value={form.service_id}
                  onChange={(e) => handleServiceChange(e.target.value)}
                  required
                >
                  <option value="">Select a service</option>
                  {filteredServices.length > 0 ? (
                    filteredServices.map(s => (
                      <option key={s.id} value={s.id}>
                        [{s.id}] {s.name} — {formatCurrencyWithConversion(s.final_price, user?.currency || 'USD', 3)}/1K
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No services available in this category</option>
                  )}
                </select>
              </div>

              {/* ── 3. Guaranteed Service Info Box (Category, Rates, Limits) ── */}
              {selectedService && (
                <div className="new-order-service-info-card">
                  <div className="service-info-header">
                    <PlatformIcon platform={detectPlatform(selectedService.name || selectedService.category)} size={34} iconSize={18} />
                    <div className="service-info-header-text">
                      <div className="service-info-badge-row">
                        <span className="service-id-pill">#{selectedService.id}</span>
                        {/* Always available category badge */}
                        <span className="service-category-pill">
                          <Folder size={11} /> {selectedService.category || activeCategoryName}
                        </span>
                      </div>
                      <h4 className="service-info-name">{selectedService.name}</h4>
                    </div>
                  </div>

                  <div className="service-info-metrics-grid">
                    <div className="service-info-metric">
                      <span className="metric-label">{t('new_order.rate_per_1k', 'Rate / 1K')}</span>
                      <span className="metric-value green">
                        {formatCurrencyWithConversion(selectedService.final_price, user?.currency || 'USD', 3)}
                      </span>
                    </div>

                    <div className="service-info-metric">
                      <span className="metric-label">{t('common.min_max', 'Min / Max')}</span>
                      <span className="metric-value">
                        {selectedService.min?.toLocaleString()} / {selectedService.max?.toLocaleString()}
                      </span>
                    </div>

                    <div className="service-info-metric">
                      <span className="metric-label">{t('new_order.delivery_time', 'Delivery Time')}</span>
                      <span className="metric-value">{deliveryTimeLabel}</span>
                    </div>
                  </div>

                  {selectedService.description && (
                    <div className="service-info-description">
                      <div className="description-label">{t('new_order.service_description', 'Service Description')}</div>
                      <div className="description-text">{selectedService.description}</div>
                    </div>
                  )}
                </div>
              )}

              {/* ── 4. Target Link / URL ── */}
              <div className="form-group">
                <div className="new-order-label-row">
                  <label className="form-label">{t('new_order.link_label', 'Link / Target URL')}</label>
                  <span className="label-helper-text">{t('new_order.link_helper', 'Make sure the profile or post is public')}</span>
                </div>
                <input
                  className="form-input"
                  type="url"
                  value={form.link}
                  onChange={e => setForm(f => ({ ...f, link: e.target.value }))}
                  placeholder={t('new_order.link_placeholder', 'https://instagram.com/yourprofile or post URL')}
                  required
                />
              </div>

              {/* ── 5. Custom Comments (if enabled) ── */}
              {selectedService?.custom_comments_enabled ? (
                <div className="form-group">
                  <div className="new-order-label-row">
                    <label className="form-label">Custom Comments</label>
                    <span className="comments-count-badge">
                      {enteredCommentCount} comment{enteredCommentCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="custom-comments-editor">
                    <div className="custom-comments-line-numbers" ref={commentNumbersRef} aria-hidden="true">
                      {commentLineNumbers.map(number => (
                        <div key={number}>{number}</div>
                      ))}
                    </div>
                    <textarea
                      className="form-input custom-comments-textarea"
                      value={form.comments}
                      onChange={e => setForm(f => ({ ...f, comments: e.target.value }))}
                      onScroll={handleCommentsScroll}
                      placeholder="Enter one comment per line..."
                      rows={5}
                      required
                      wrap="off"
                    />
                  </div>
                </div>
              ) : null}

              {/* ── 6. Quantity Field & Quick Presets ── */}
              <div className="form-group">
                <div className="new-order-label-row">
                  <label className="form-label">
                    {t('new_order.quantity_label', 'Quantity')}
                    {selectedService && (
                      <span className="label-range">
                        (min {selectedService.min.toLocaleString()} — max {selectedService.max.toLocaleString()})
                      </span>
                    )}
                  </label>
                </div>

                <input
                  className="form-input"
                  style={!qtyValid && form.quantity ? { borderColor: 'var(--red-500)' } : {}}
                  type="number"
                  value={form.quantity}
                  onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                  min={selectedService?.min}
                  max={selectedService?.max}
                  placeholder={t('new_order.quantity_label', 'Enter quantity')}
                  required
                />

                {!qtyValid && form.quantity && (
                  <div className="quantity-error-msg">
                    <AlertCircle size={13} />
                    <span>Quantity must be between {selectedService?.min?.toLocaleString()} and {selectedService?.max?.toLocaleString()}</span>
                  </div>
                )}

                {/* Quick Presets (Matching Mobile App) */}
                <div className="quantity-presets-row">
                  {QUANTITY_PRESETS.map((preset) => {
                    const minOk = !selectedService || preset >= selectedService.min;
                    const maxOk = !selectedService || preset <= selectedService.max;
                    if (!minOk || !maxOk) return null;
                    const isSelected = String(preset) === form.quantity;
                    return (
                      <button
                        key={preset}
                        type="button"
                        className={`quantity-preset-chip ${isSelected ? 'active' : ''}`}
                        onClick={() => setForm(f => ({ ...f, quantity: String(preset) }))}
                      >
                        {preset.toLocaleString()}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── 7. Order Summary & Wallet Card ── */}
              <div className="new-order-summary-card">
                <div className="summary-row">
                  <span className="summary-label">{t('common.rate_per_1k', 'Price per 1,000')}</span>
                  <span className="summary-value">
                    {selectedService ? formatCurrencyWithConversion(selectedService.final_price, user?.currency || 'USD', 3) : '-'}
                  </span>
                </div>

                <div className="summary-row">
                  <span className="summary-label">{t('new_order.quantity_label', 'Quantity')}</span>
                  <span className="summary-value">{qty > 0 ? qty.toLocaleString() : '-'}</span>
                </div>

                <div className="summary-divider" />

                <div className="summary-total-row">
                  <span>{t('new_order.total_price', 'Total Cost')}</span>
                  <span className="summary-total-price">
                    {formatCurrencyWithConversion(costUSD, user?.currency || 'USD', 3)}
                  </span>
                </div>

                <div className="summary-balance-row">
                  <span className="summary-label">{t('wallet.current_balance', 'Account Balance')}</span>
                  <span className={`summary-balance-val ${costUSD > 0 && !canAfford ? 'insufficient' : 'sufficient'}`}>
                    {formatCurrencyWithConversion(balanceUSD, user?.currency || 'USD', 3)}
                    {costUSD > 0 && !canAfford && ` (${t('new_order.insufficient_balance', 'Insufficient')})`}
                  </span>
                </div>

                {costUSD > 0 && !canAfford && (
                  <Link to="/dashboard/deposit" className="summary-deposit-link">
                    {t('new_order.deposit_now', 'Top up to place order')} {formatCurrencyWithConversion(costUSD - balanceUSD, user?.currency || 'USD', 3)} {'→'}
                  </Link>
                )}
              </div>

              {/* ── 8. Submit Button ── */}
              <button
                type="submit"
                className="btn btn-primary btn-lg new-order-submit-btn"
                disabled={loading || !form.service_id || !form.link || !qtyValid || !canAfford}
              >
                {loading ? (
                  <span className="submit-loading-wrap">
                    <span className="spinner" /> {t('common.loading', 'Placing order...')}
                  </span>
                ) : (
                  <span className="submit-ready-wrap">
                    {t('new_order.place_order_btn', 'Place Order')} <ArrowRight size={18} />
                  </span>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
